import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { ArrowLeft, ArrowRight, Edit3, ExternalLink, Megaphone, Phone, Plus, Save, Zap, Share2, X } from 'lucide-react';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { NaturalLanguageFiller } from '@/components/NaturalLanguageFiller';
import { AccountCombobox } from '@/components/AccountCombobox';
import { AttachmentPanel } from '@/components/AttachmentPanel';
import { CloseReasonPicker, type CloseCategory } from '@/components/CloseReasonPicker';
import { CustomFieldsPanel } from '@/components/CustomFieldsPanel';
import { FormField, FormGrid, FormSection } from '@/components/forms/FormSection';
import { apiClient } from '@/lib/apiClient';
import { useAuthStore } from '@/stores/authStore';

interface EmailDraft {
  subject: string;
  body: string;
}
interface AiSdrOutput {
  firstTouchEmail: EmailDraft;
  followUpCadence: EmailDraft[];
  nextBestAction: string;
}

interface LeadContact {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  title: string | null;
}

interface ProcessTemplateSummary {
  id: string;
  name: string;
  description: string | null;
  scope: string;
}

interface LeadDetail {
  id: string;
  tenantId: string;
  campaignId: string | null;
  source: string;
  status: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  companyName: string | null;
  qualificationNote: string | null;
  assignedRepId: string | null;
  accountId: string | null;
  accountName: string | null;
  contacts: LeadContact[];
  convertedOpportunityId: string | null;
  convertedOpportunityName: string | null;
  // campaignName / campaignStatus travel with campaignId from GET /leads/{id}.
  // Optional so a client pointed at an older API still type-checks: the header
  // then falls back to a plain link instead of the named chip.
  campaignName?: string | null;
  campaignStatus?: string | null;
  campaignFarewellTemplate: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  // Attribution, consent and free-text. GET /leads/{id} returns all of these
  // (LeadDetail in internal/leads/service.go); they stay optional so a client
  // pointed at an older API still type-checks and renders '—'.
  channel?: string | null;
  capturedAt?: string | null;
  emailStatus?: string | null;
  externalLeadRef?: string | null;
  utmSource?: string | null;
  utmMedium?: string | null;
  utmCampaign?: string | null;
  utmContent?: string | null;
  utmTerm?: string | null;
  country?: string | null;
  languagePreference?: string | null;
  consentCapturedAt?: string | null;
  consentSource?: string | null;
  leadScore?: number | null;
  disqualificationReason?: string | null;
  notes?: string | null;
  tags?: string[] | null;
}

const STATUS_CLASSES: Record<string, string> = {
  NEW: 'bg-blue-900/40 text-blue-200',
  QUALIFIED: 'bg-green-900/40 text-green-200',
  DISCARDED: 'bg-muted text-muted-foreground',
  CONVERTED: 'bg-emerald-900/40 text-emerald-200',
};

export function LeadDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const userId = useAuthStore((s) => s.user?.userId);
  const [lead, setLead] = useState<LeadDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [transitioning, setTransitioning] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // ─── Full-lead edit mode (Epic forms-uplift / leads.edit) ───
  // Drafts for every editable identity field. We keep them as
  // separate hooks rather than a single object so React's batching
  // and tree-diff stays cheap for typed input.
  const [editMode, setEditMode] = useState(false);
  const [savingLead, setSavingLead] = useState(false);
  const [draftFirstName, setDraftFirstName] = useState('');
  const [draftLastName, setDraftLastName] = useState('');
  const [draftEmail, setDraftEmail] = useState('');
  const [draftPhone, setDraftPhone] = useState('');
  const [draftCompanyName, setDraftCompanyName] = useState('');
  const [draftSource, setDraftSource] = useState('');
  const [draftChannel, setDraftChannel] = useState('');
  const [draftCountry, setDraftCountry] = useState('');
  const [draftLanguagePreference, setDraftLanguagePreference] = useState('');
  const [draftUtmSource, setDraftUtmSource] = useState('');
  const [draftUtmMedium, setDraftUtmMedium] = useState('');
  const [draftUtmCampaign, setDraftUtmCampaign] = useState('');
  const [draftLeadScore, setDraftLeadScore] = useState('');

  // Convert-to-opportunity state
  const [convertOpen, setConvertOpen] = useState(false);
  const [templates, setTemplates] = useState<ProcessTemplateSummary[] | null>(null);
  const [convertAccounts, setConvertAccounts] = useState<Array<{ id: string; name: string }>>([]);
  const [convertAccountId, setConvertAccountId] = useState<string>('');
  const [convertName, setConvertName] = useState('');
  const [convertValue, setConvertValue] = useState('0');
  const [convertCloseDate, setConvertCloseDate] = useState('');
  const [convertTemplateId, setConvertTemplateId] = useState<string>('');
  const [converting, setConverting] = useState(false);
  const [convertError, setConvertError] = useState<string | null>(null);

  // Discard-with-farewell state (story 48.11)
  const [discardOpen, setDiscardOpen] = useState(false);
  const [farewellMessage, setFarewellMessage] = useState('');
  // Epic 86 — discard reason taxonomy
  const [discardReasonCode, setDiscardReasonCode] = useState('');
  const [discardReasonCategory, setDiscardReasonCategory] = useState<CloseCategory | ''>('');

  // AI SDR draft state (Epic 66.1)
  const [aiSdrDraft, setAiSdrDraft] = useState<AiSdrOutput | null>(null);
  const [aiSdrLoading, setAiSdrLoading] = useState(false);
  const [aiSdrError, setAiSdrError] = useState<string | null>(null);

  // Click-to-call DNC check (Epic 64.1)
  const [dncBlocked, setDncBlocked] = useState<boolean | null>(null);
  // Handoff state (story 59.2)
  const userRole = useAuthStore((s) => s.user?.role);
  const canHandoff = userRole === 'SDR' || userRole === 'PRE_SALES';
  // Epic 86 — manager+ guard for DATA_QUALITY discard codes
  const allowDataQuality =
    userRole === 'SALES_MANAGER' || userRole === 'TENANT_ADMIN' || userRole === 'PLATFORM_ADMIN';
  const [handoffOpen, setHandoffOpen] = useState(false);
  const [handoffNote, setHandoffNote] = useState('');
  const [handoffPending, setHandoffPending] = useState(false);
  const [submittingHandoff, setSubmittingHandoff] = useState(false);
  // Sequence enrollment state (story 61.4)
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [sequences, setSequences] = useState<{ id: string; name: string }[]>([]);
  const [selectedSequenceId, setSelectedSequenceId] = useState('');
  const [enrolling, setEnrolling] = useState(false);

  // Add-contact state (story 46.5)
  const [contactFormOpen, setContactFormOpen] = useState(false);
  const [contactFirstName, setContactFirstName] = useState('');
  const [contactLastName, setContactLastName] = useState('');
  const [contactTitle, setContactTitle] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactSaving, setContactSaving] = useState(false);
  const [contactError, setContactError] = useState<string | null>(null);

  // Story 48.10 — live SSE stream status
  const [streamStatus, setStreamStatus] = useState<'idle' | 'connected' | 'error'>('idle');

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    apiClient
      .get<LeadDetail>(`/leads/${id}`)
      .then(({ data }) => {
        if (cancelled) return;
        setLead(data);
        setNoteDraft(data.qualificationNote ?? '');
      })
      .catch((err) => {
        if (cancelled) return;
        if (isAxiosError(err) && err.response?.status === 404) {
          setNotFound(true);
        } else {
          setToast(t('lead.loading'));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, t]);

  // Story 48.10 — subscribe to SSE stream for live updates.
  useEffect(() => {
    if (!id || !lead) return;
    const baseUrl = (apiClient.defaults.baseURL ?? '').replace(/\/$/, '');
    const token = apiClient.defaults.headers?.common?.['Authorization'];
    const url = `${baseUrl}/leads/${id}/stream`;
    // EventSource doesn't support custom headers natively; pass the JWT
    // as a query param. The backend @PreAuthorize("isAuthenticated()") still
    // validates it via the standard Spring Security filter chain.
    const tokenStr = typeof token === 'string' ? token.replace('Bearer ', '') : '';
    const sse = new EventSource(tokenStr ? `${url}?token=${encodeURIComponent(tokenStr)}` : url, {
      withCredentials: false,
    });

    sse.addEventListener('connected', () => {
      setStreamStatus('connected');
    });

    sse.addEventListener('lead-updated', (e) => {
      try {
        const updated = JSON.parse((e as MessageEvent).data) as Partial<LeadDetail>;
        setLead((prev) => (prev ? { ...prev, ...updated } : prev));
        setToast(t('lead.live_update'));
        setTimeout(() => setToast(null), 2500);
      } catch {
        // Malformed event — ignore
      }
    });

    sse.onerror = () => {
      setStreamStatus('error');
    };

    return () => {
      sse.close();
      setStreamStatus('idle');
    };
  }, [id, lead?.id, t]); // eslint-disable-line react-hooks/exhaustive-deps

  async function saveNote() {
    if (!lead) return;
    const note = noteDraft.trim().length > 0 ? noteDraft : null;
    setSaving(true);
    try {
      await apiClient.patch(`/leads/${lead.id}`, { qualificationNote: note });
      setLead({ ...lead, qualificationNote: note });
      setToast('Note saved.');
    } catch {
      setToast('Could not save note.');
    } finally {
      setSaving(false);
      setTimeout(() => setToast(null), 2500);
    }
  }

  // ─── Edit mode helpers ───
  function seedDrafts(src: LeadDetail) {
    setDraftFirstName(src.firstName ?? '');
    setDraftLastName(src.lastName ?? '');
    setDraftEmail(src.email ?? '');
    setDraftPhone(src.phone ?? '');
    setDraftCompanyName(src.companyName ?? '');
    setDraftSource(src.source ?? '');
    setDraftChannel(src.channel ?? '');
    setDraftCountry(src.country ?? '');
    setDraftLanguagePreference(src.languagePreference ?? '');
    setDraftUtmSource(src.utmSource ?? '');
    setDraftUtmMedium(src.utmMedium ?? '');
    setDraftUtmCampaign(src.utmCampaign ?? '');
    setDraftLeadScore(src.leadScore != null ? String(src.leadScore) : '');
  }

  function openEdit() {
    if (!lead) return;
    seedDrafts(lead);
    setEditMode(true);
  }

  function cancelEdit() {
    setEditMode(false);
  }

  /**
   * Save all editable identity fields as a single PATCH.
   *
   * Field-level semantics, matching UpdateLeadFields in
   * internal/leads/queries.go one for one:
   *   - undefined / not in body  → leave unchanged
   *   - empty string             → clear (set to NULL in DB)
   *   - non-empty string         → set
   *
   * Every key sent below is persisted server-side. leadScore is the
   * exception to the string rule: it travels as a number, or as an
   * explicit null to clear (UpdateLeadRequest.LeadScore is a tri-state,
   * so absent and null are not the same thing).
   */
  async function saveLead(e?: FormEvent) {
    if (e) e.preventDefault();
    if (!lead) return;
    const body: Record<string, unknown> = {};
    const apply = (key: string, draft: string, current: string | null | undefined) => {
      if (draft !== (current ?? '')) {
        body[key] = draft.trim().length > 0 ? draft.trim() : '';
      }
    };
    apply('firstName', draftFirstName, lead.firstName);
    apply('lastName', draftLastName, lead.lastName);
    apply('email', draftEmail, lead.email);
    apply('phone', draftPhone, lead.phone);
    apply('companyName', draftCompanyName, lead.companyName);
    apply('source', draftSource, lead.source);
    apply('channel', draftChannel, lead.channel);
    apply('country', draftCountry, lead.country);
    apply('languagePreference', draftLanguagePreference, lead.languagePreference);
    apply('utmSource', draftUtmSource, lead.utmSource);
    apply('utmMedium', draftUtmMedium, lead.utmMedium);
    apply('utmCampaign', draftUtmCampaign, lead.utmCampaign);
    const scoreParsed = draftLeadScore.trim() ? Number(draftLeadScore) : null;
    if (scoreParsed !== (lead.leadScore ?? null)) {
      body.leadScore = scoreParsed;
    }
    if (Object.keys(body).length === 0) {
      setEditMode(false);
      return;
    }
    setSavingLead(true);
    try {
      await apiClient.patch(`/leads/${lead.id}`, body);
      // Optimistic merge — the SSE channel may also push an update.
      setLead((prev) =>
        prev
          ? {
              ...prev,
              firstName: 'firstName' in body ? (body.firstName as string) || null : prev.firstName,
              lastName: 'lastName' in body ? (body.lastName as string) || null : prev.lastName,
              email: 'email' in body ? (body.email as string) || null : prev.email,
              phone: 'phone' in body ? (body.phone as string) || null : prev.phone,
              companyName:
                'companyName' in body ? (body.companyName as string) || null : prev.companyName,
              source: 'source' in body && body.source ? (body.source as string) : prev.source,
              channel: 'channel' in body ? (body.channel as string) || null : prev.channel,
              country: 'country' in body ? (body.country as string) || null : prev.country,
              languagePreference:
                'languagePreference' in body
                  ? (body.languagePreference as string) || null
                  : prev.languagePreference,
              utmSource: 'utmSource' in body ? (body.utmSource as string) || null : prev.utmSource,
              utmMedium: 'utmMedium' in body ? (body.utmMedium as string) || null : prev.utmMedium,
              utmCampaign:
                'utmCampaign' in body ? (body.utmCampaign as string) || null : prev.utmCampaign,
              leadScore: 'leadScore' in body ? (body.leadScore as number | null) : prev.leadScore,
            }
          : prev,
      );
      setEditMode(false);
      setToast(t('lead.edit.saved'));
    } catch (err) {
      const detail = isAxiosError(err)
        ? (err.response?.data as { detail?: string } | undefined)?.detail
        : null;
      setToast(detail ?? t('lead.edit.saveError'));
    } finally {
      setSavingLead(false);
      setTimeout(() => setToast(null), 3000);
    }
  }

  async function openConvert() {
    if (!lead) return;
    setConvertError(null);
    setConvertName(
      [lead.companyName, [lead.firstName, lead.lastName].filter(Boolean).join(' ')]
        .filter((s) => s && s.length > 0)
        .join(' — ') || 'Opportunity',
    );
    setConvertValue('0');
    setConvertCloseDate('');
    setConvertAccountId('');
    setConvertOpen(true);
    if (templates === null) {
      try {
        const { data } = await apiClient.get<ProcessTemplateSummary[]>('/process-templates');
        setTemplates(data);
        if (data.length > 0) setConvertTemplateId(data[0].id);
      } catch {
        setTemplates([]);
      }
    } else if (templates.length > 0 && !convertTemplateId) {
      setConvertTemplateId(templates[0].id);
    }
    // Always load the account list so the conversion form picker is
    // populated. Manually-created leads have no LEADS.ACCOUNT_ID and need
    // an explicit pick; leads created from the lead-capture form may
    // already have one but we show the picker anyway with the linked
    // account preselected — that way a rep who needs to change the
    // account at conversion time can do so without going back to edit
    // the lead.
    if (convertAccounts.length === 0) {
      try {
        const { data } = await apiClient.get<Array<{ id: string; name: string }>>('/accounts');
        setConvertAccounts(data);
      } catch {
        setConvertAccounts([]);
      }
    }
    if (lead?.accountId) {
      setConvertAccountId(lead.accountId);
    }
  }

  async function submitConvert() {
    if (!lead) return;
    setConvertError(null);
    setConverting(true);
    const payload: Record<string, unknown> = {
      name: convertName.trim() || null,
      estimatedDealValue: Number(convertValue) || 0,
    };
    if (convertCloseDate) payload.expectedCloseDate = convertCloseDate;
    if (convertTemplateId) payload.templateId = convertTemplateId;
    // Always pass the picked account if the user changed it (or set it
    // for the first time). The backend treats overrides.accountId as
    // taking precedence over lead.accountId, so this is a no-op when
    // the rep didn't touch the picker.
    if (convertAccountId) payload.accountId = convertAccountId;
    try {
      const { data } = await apiClient.post<{ id: string }>(
        `/opportunities/from-lead/${lead.id}`,
        payload,
      );
      navigate(`/opportunities/${data.id}`);
    } catch (err) {
      if (isAxiosError(err)) {
        const detail = (err.response?.data as { detail?: string } | undefined)?.detail;
        setConvertError(detail ?? 'Could not convert lead to opportunity.');
      } else {
        setConvertError('Could not convert lead to opportunity.');
      }
      setConverting(false);
    }
  }

  async function transition(action: 'qualify' | 'discard' | 'revert') {
    if (!lead) return;
    setTransitioning(action);
    try {
      await apiClient.post(`/leads/${lead.id}/${action}`);
      if (action === 'discard') {
        navigate('/leads', { replace: true });
        return;
      }
      const { data } = await apiClient.get<LeadDetail>(`/leads/${lead.id}`);
      setLead(data);
      setNoteDraft(data.qualificationNote ?? '');
      setToast(`Lead ${action === 'qualify' ? 'qualified' : 'reverted to NEW'}.`);
    } catch {
      setToast(`Could not ${action} lead.`);
    } finally {
      setTransitioning(null);
      setTimeout(() => setToast(null), 2500);
    }
  }

  async function submitNewContact() {
    if (!lead?.accountId) return;
    setContactError(null);
    if (!contactFirstName.trim() || !contactLastName.trim()) {
      setContactError('First and last name are required.');
      return;
    }
    setContactSaving(true);
    try {
      await apiClient.post('/contacts', {
        accountId: lead.accountId,
        firstName: contactFirstName.trim(),
        lastName: contactLastName.trim(),
        title: contactTitle.trim() || null,
        email: contactEmail.trim() || null,
        phone: contactPhone.trim() || null,
      });
      // Refetch the lead so the new contact shows up in the list.
      const { data } = await apiClient.get<LeadDetail>(`/leads/${lead.id}`);
      setLead(data);
      setContactFirstName('');
      setContactLastName('');
      setContactTitle('');
      setContactEmail('');
      setContactPhone('');
      setContactFormOpen(false);
      setToast('Contact added.');
      setTimeout(() => setToast(null), 2000);
    } catch (err) {
      if (isAxiosError(err)) {
        const detail = (err.response?.data as { detail?: string } | undefined)?.detail;
        setContactError(detail ?? 'Could not add contact.');
      } else {
        setContactError('Could not add contact.');
      }
    } finally {
      setContactSaving(false);
    }
  }

  async function generateAiSdrDraft() {
    if (!lead) return;
    setAiSdrLoading(true);
    setAiSdrError(null);
    try {
      const r = await apiClient.post('/api/v1/agents/A13_AI_SDR/run', {
        input: {
          firstName: lead.firstName ?? '',
          lastName: lead.lastName ?? '',
          companyName: lead.companyName ?? '',
          email: lead.email ?? '',
          phone: lead.phone ?? '',
          status: lead.status,
          source: lead.source,
        },
      });
      setAiSdrDraft(r.data?.output ?? r.data);
    } catch {
      setAiSdrError(t('aiSdr.error'));
    } finally {
      setAiSdrLoading(false);
    }
  }

  async function checkDncForLead() {
    if (!lead?.phone) return;
    try {
      const r = await apiClient.get(`/api/v1/dialer/dnc-check?phone=${encodeURIComponent(lead.phone)}`);
      setDncBlocked(r.data.blocked);
    } catch {
      setDncBlocked(false);
    }
  }

  async function clickToCall() {
    if (!lead?.phone || !userId) return;
    try {
      await apiClient.post('/api/v1/dialer/calls', {
        userId,
        recipientPhone: lead.phone,
        leadId: lead.id,
      });
      setToast(t('common.saved'));
    } catch {
      setToast(t('dialer.call_blocked_dnc'));
    }
  }

  function openDiscardDialog() {
    if (!lead) return;
    const template = lead.campaignFarewellTemplate ?? '';
    const rendered = template
      .replace(/\{\{firstName\}\}/g, lead.firstName ?? '')
      .replace(/\{\{lastName\}\}/g, lead.lastName ?? '')
      .replace(/\{\{companyName\}\}/g, lead.companyName ?? '');
    setFarewellMessage(rendered);
    setDiscardOpen(true);
  }

  async function submitDiscard() {
    if (!lead) return;
    if (!discardReasonCode) return;
    setTransitioning('discard');
    try {
      const trimmed = farewellMessage.trim();
      const body: Record<string, unknown> = {
        code: discardReasonCode,
        category: discardReasonCategory,
      };
      if (trimmed) body.note = `Farewell: ${trimmed}`;
      await apiClient.post(`/leads/${lead.id}/discard`, body);
      navigate('/leads', { replace: true });
    } catch {
      setToast('Could not discard lead.');
      setTransitioning(null);
      setTimeout(() => setToast(null), 2500);
    }
  }

  async function submitHandoff() {
    if (!lead) return;
    setSubmittingHandoff(true);
    try {
      await apiClient.post(`/leads/${lead.id}/handoff`, { note: handoffNote.trim() || null });
      setHandoffPending(true);
      setHandoffOpen(false);
      setToast(t('handoff.success'));
      setTimeout(() => setToast(null), 3000);
    } catch {
      setToast(t('handoff.error_submit'));
      setTimeout(() => setToast(null), 3000);
    } finally {
      setSubmittingHandoff(false);
    }
  }

  if (loading) {
    return <div className="p-6 text-sm text-muted-foreground">{t('lead.loading')}</div>;
  }
  if (notFound || !lead) {
    return (
      <div className="p-6">
        <Link to="/leads" className="inline-flex items-center gap-1 text-sm text-primary">
          <ArrowLeft className="h-4 w-4" /> {t('lead.back_to_leads')}
        </Link>
        <p className="mt-4 text-sm text-muted-foreground">{t('lead.not_found')}</p>
      </div>
    );
  }

  const displayName =
    [lead.firstName, lead.lastName].filter(Boolean).join(' ') || lead.companyName || '—';

  // The route is /leads/:id, and leads.campaign_id is nullable attribution, not
  // containment — a lead does not live inside its campaign. So the trail mirrors
  // the router and always keeps the way back to the list; the campaign is a
  // first-class relation in the header below, next to account and opportunity.
  const breadcrumbCrumbs = [{ label: t('nav.leads'), to: '/leads' }, { label: displayName }];

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-6">
      <Breadcrumbs crumbs={breadcrumbCrumbs} />
      <div className="flex items-center justify-between">
        <Link to="/leads" className="inline-flex items-center gap-1 text-sm text-primary">
          <ArrowLeft className="h-4 w-4" /> {t('lead.back_to_leads')}
        </Link>
        <div className="flex items-center gap-2">
          {streamStatus === 'connected' && (
            <span className="inline-flex items-center gap-1 rounded-full bg-green-900/40 px-2 py-0.5 text-xs text-green-300">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-green-500" />
              {t('lead.stream_connected')}
            </span>
          )}
          {streamStatus === 'error' && (
            <span className="rounded-full bg-yellow-900/40 px-2 py-0.5 text-xs text-yellow-300">
              {t('lead.stream_error')}
            </span>
          )}
          <span
            className={`rounded-full px-3 py-1 text-xs font-medium ${
              STATUS_CLASSES[lead.status] ?? 'bg-muted text-muted-foreground'
            }`}
          >
            {lead.status}
          </span>
        </div>
      </div>

      {/* Header */}
      <div className="rounded-lg border bg-card p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold">{displayName}</h1>
            {lead.companyName && displayName !== lead.companyName && (
              <div className="mt-1 text-sm text-muted-foreground">{lead.companyName}</div>
            )}
          </div>
          {!editMode && (
            <Button size="sm" variant="outline" onClick={openEdit}>
              <Edit3 className="mr-1 h-3.5 w-3.5" />
              {t('lead.edit.button')}
            </Button>
          )}
        </div>
        <div className="mt-4 grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              {t('common.phone')}
            </div>
            <div className="mt-0.5 flex items-center gap-2">
              <span className="font-medium">{lead.phone ?? '—'}</span>
              {lead.phone && (
                <button
                  onClick={() => {
                    checkDncForLead();
                    clickToCall();
                  }}
                  disabled={dncBlocked === true}
                  title={dncBlocked === true ? t('dialer.call_blocked_dnc') : t('dialer.call_button')}
                  className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs transition-colors ${
                    dncBlocked === true
                      ? 'cursor-not-allowed bg-muted text-muted-foreground opacity-50'
                      : 'bg-primary/10 text-primary hover:bg-primary/20'
                  }`}
                >
                  <Phone className="h-3 w-3" />
                  {t('dialer.call_button')}
                </button>
              )}
            </div>
          </div>
          <Field label={t('common.email')} value={lead.email ?? '—'} />
          <Field label={t('common.source')} value={lead.source} />
          <Field label={t('common.created')} value={formatDate(lead.createdAt)} />
        </div>

        {/* Relations. Campaign leads the block because it answers "where did
            this lead come from", which the breadcrumb deliberately no longer
            claims to say. */}
        {lead.campaignId && (
          <div className="mt-4 flex flex-wrap items-center gap-2 text-sm">
            <span className="text-muted-foreground">{t('lead.campaign')}:</span>
            <Link
              to={`/campaigns/${lead.campaignId}`}
              className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-1 font-medium text-primary hover:bg-primary/20"
            >
              <Megaphone className="h-3.5 w-3.5 shrink-0" />
              {lead.campaignName ?? t('lead.open_campaign')}
              <ExternalLink className="h-3.5 w-3.5 shrink-0" />
            </Link>
            {lead.campaignStatus && (
              <span
                className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${
                  lead.campaignStatus === 'ACTIVE'
                    ? 'bg-emerald-900/40 text-emerald-200'
                    : lead.campaignStatus === 'PAUSED'
                      ? 'bg-amber-900/40 text-amber-200'
                      : lead.campaignStatus === 'CLOSED'
                        ? 'bg-muted text-foreground'
                        : 'bg-blue-900/40 text-blue-200'
                }`}
              >
                {lead.campaignStatus}
              </span>
            )}
          </div>
        )}

        {lead.accountId && (
          <div className="mt-2 inline-flex items-center gap-1 text-sm">
            <span className="text-muted-foreground">{t('lead.account')}:</span>
            <Link
              to={`/accounts/${lead.accountId}`}
              className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
            >
              {lead.accountName ?? t('lead.open_account')}
              <ExternalLink className="h-3.5 w-3.5" />
            </Link>
          </div>
        )}

        {lead.convertedOpportunityId && (
          <div className="mt-2 inline-flex items-center gap-1 text-sm">
            <span className="text-muted-foreground">{t('lead.opportunity')}:</span>
            <Link
              to={`/opportunities/${lead.convertedOpportunityId}`}
              className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
            >
              {lead.convertedOpportunityName ?? t('lead.open_opportunity')}
              <ExternalLink className="h-3.5 w-3.5" />
            </Link>
          </div>
        )}
      </div>

      {/* ─── All lead fields ─── */}
      <form onSubmit={saveLead} className="space-y-3">
        <FormSection title={t('lead.section.identity')} defaultOpen>
          {editMode ? (
            <>
              <FormGrid>
                <FormField label={t('newLead.first_name')}>
                  <Input value={draftFirstName} onChange={(e) => setDraftFirstName(e.target.value)} />
                </FormField>
                <FormField label={t('newLead.last_name')}>
                  <Input value={draftLastName} onChange={(e) => setDraftLastName(e.target.value)} />
                </FormField>
              </FormGrid>
              <FormGrid>
                <FormField label={t('newLead.email')}>
                  <Input type="email" value={draftEmail} onChange={(e) => setDraftEmail(e.target.value)} />
                </FormField>
                <FormField label={t('newLead.phone')}>
                  <Input value={draftPhone} onChange={(e) => setDraftPhone(e.target.value)} />
                </FormField>
              </FormGrid>
              <FormField label={t('newLead.company')}>
                <Input value={draftCompanyName} onChange={(e) => setDraftCompanyName(e.target.value)} />
              </FormField>
              <FormGrid>
                <FormField label={t('newLead.field.country')}>
                  <Input value={draftCountry} onChange={(e) => setDraftCountry(e.target.value.toUpperCase())} maxLength={2} placeholder="PR" />
                </FormField>
                <FormField label={t('newLead.field.languagePreference')}>
                  <Input value={draftLanguagePreference} onChange={(e) => setDraftLanguagePreference(e.target.value)} placeholder="es / en / pt-BR" />
                </FormField>
              </FormGrid>
            </>
          ) : (
            <FormGrid>
              <ReadField label={t('newLead.first_name')} value={lead.firstName} />
              <ReadField label={t('newLead.last_name')} value={lead.lastName} />
              <ReadField label={t('newLead.email')} value={lead.email} />
              <ReadField label={t('newLead.phone')} value={lead.phone} />
              <ReadField label={t('newLead.company')} value={lead.companyName} />
              <ReadField label={t('newLead.field.country')} value={lead.country} />
              <ReadField label={t('newLead.field.languagePreference')} value={lead.languagePreference} />
            </FormGrid>
          )}
        </FormSection>

        <FormSection title={t('lead.section.attribution')} defaultOpen={false}>
          {editMode ? (
            <>
              <FormGrid>
                <FormField label={t('common.source')}>
                  <Input value={draftSource} onChange={(e) => setDraftSource(e.target.value)} />
                </FormField>
                <FormField label={t('lead.field.channel')}>
                  <Input value={draftChannel} onChange={(e) => setDraftChannel(e.target.value.toUpperCase())} placeholder="WEB_FORM" />
                </FormField>
              </FormGrid>
              <FormGrid>
                <FormField label={t('newLead.field.utmSource')}>
                  <Input value={draftUtmSource} onChange={(e) => setDraftUtmSource(e.target.value)} />
                </FormField>
                <FormField label={t('newLead.field.utmMedium')}>
                  <Input value={draftUtmMedium} onChange={(e) => setDraftUtmMedium(e.target.value)} />
                </FormField>
              </FormGrid>
              <FormField label={t('newLead.field.utmCampaign')}>
                <Input value={draftUtmCampaign} onChange={(e) => setDraftUtmCampaign(e.target.value)} />
              </FormField>
            </>
          ) : (
            <FormGrid>
              <ReadField label={t('common.source')} value={lead.source} />
              <ReadField label={t('lead.field.channel')} value={lead.channel} />
              <ReadField label={t('newLead.field.utmSource')} value={lead.utmSource} />
              <ReadField label={t('newLead.field.utmMedium')} value={lead.utmMedium} />
              <ReadField label={t('newLead.field.utmCampaign')} value={lead.utmCampaign} />
              <ReadField label={t('newLead.field.utmContent')} value={lead.utmContent} />
              <ReadField label={t('newLead.field.utmTerm')} value={lead.utmTerm} />
              <ReadField label={t('newLead.field.consentSource')} value={lead.consentSource} />
              <ReadField label={t('newLead.field.consentCapturedAt')} value={lead.consentCapturedAt} />
            </FormGrid>
          )}
        </FormSection>

        <FormSection title={t('lead.section.scoring')} defaultOpen={false}>
          {editMode ? (
            <FormField label={t('newLead.field.leadScore')} hint="0–100">
              <Input
                type="number"
                min={0}
                max={100}
                value={draftLeadScore}
                onChange={(e) => setDraftLeadScore(e.target.value)}
              />
            </FormField>
          ) : (
            <FormGrid>
              <ReadField label={t('newLead.field.leadScore')} value={lead.leadScore != null ? String(lead.leadScore) : null} />
              <ReadField label={t('newLead.field.disqualificationReason')} value={lead.disqualificationReason} />
              <ReadField label={t('common.status')} value={lead.status} />
            </FormGrid>
          )}
        </FormSection>

        <FormSection title={t('lead.section.metadata')} defaultOpen={false}>
          <FormGrid>
            <ReadField label={t('common.created')} value={formatDate(lead.createdAt)} />
            <ReadField label={t('common.updated')} value={formatDate(lead.updatedAt)} />
            <ReadField label={t('lead.field.capturedAt')} value={formatDate(lead.capturedAt ?? null)} />
            <ReadField label={t('lead.field.emailStatus')} value={lead.emailStatus} />
            <ReadField label={t('lead.field.externalRef')} value={lead.externalLeadRef} />
            <ReadField label={t('lead.field.id')} value={lead.id} />
          </FormGrid>
          {/*
            Outside the grid and pre-wrapped: notes is free text, and for a
            voice-captured lead it is a multi-line summary written by the
            agent. A half-width ReadField would collapse its line breaks.
          */}
          <div className="space-y-1">
            <div className="text-xs font-medium text-muted-foreground">{t('lead.field.notes')}</div>
            <div className="whitespace-pre-wrap rounded-md border bg-muted/30 px-3 py-2 text-sm">
              {lead.notes && lead.notes.length > 0 ? lead.notes : '—'}
            </div>
          </div>
        </FormSection>

        {editMode && (
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" size="sm" onClick={cancelEdit} disabled={savingLead}>
              <X className="mr-1 h-3.5 w-3.5" />
              {t('common.cancel')}
            </Button>
            <Button type="submit" size="sm" disabled={savingLead}>
              <Save className="mr-1 h-3.5 w-3.5" />
              {savingLead ? t('lead.edit.saving') : t('lead.edit.save')}
            </Button>
          </div>
        )}
      </form>

      {/*
        Tenant-defined LEAD fields. Renders nothing at all for a tenant with no
        LEAD definitions (CustomFieldsPanel returns null on an empty list), so
        this is inert everywhere it does not apply.
      */}
      <CustomFieldsPanel
        entityType="LEAD"
        entityId={lead.id}
        title={t('lead.section.admissions')}
      />

      {/* Qualification note */}
      <div className="rounded-lg border bg-card p-6">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Qualification notes
          </h2>
          <NaturalLanguageFiller
            fieldSchema={[
              { name: 'firstName', label: 'First name', type: 'text' },
              { name: 'lastName', label: 'Last name', type: 'text' },
              { name: 'email', label: 'Email', type: 'email' },
              { name: 'phone', label: 'Phone', type: 'phone' },
              { name: 'companyName', label: 'Company', type: 'text' },
              { name: 'qualificationNote', label: 'Qualification note', type: 'textarea' },
            ]}
            currentValues={{
              firstName: lead.firstName ?? '',
              lastName: lead.lastName ?? '',
              email: lead.email ?? '',
              phone: lead.phone ?? '',
              companyName: lead.companyName ?? '',
              qualificationNote: noteDraft,
            }}
            onApply={(v) => {
              if (v.qualificationNote !== undefined) {
                // Append rather than replace — the rep's existing note
                // and the AI-extracted note are usually complementary.
                const existing = noteDraft.trim();
                setNoteDraft(existing ? `${existing}\n\n${v.qualificationNote}` : v.qualificationNote);
              }
              // For the named lead fields we PATCH the lead immediately
              // so the header re-renders with the new values; the user
              // can still edit before saving the note.
              const patch: Record<string, string | null> = {};
              for (const k of ['firstName', 'lastName', 'email', 'phone', 'companyName']) {
                if (v[k] !== undefined) patch[k] = v[k] || null;
              }
              if (Object.keys(patch).length > 0 && lead) {
                apiClient
                  .patch(`/leads/${lead.id}`, patch)
                  .then(() => apiClient.get<LeadDetail>(`/leads/${lead.id}`))
                  .then((res) => setLead(res.data))
                  .catch(() => { /* graceful */ });
              }
            }}
          />
        </div>
        <textarea
          className="mt-3 w-full rounded-md border bg-background p-3 text-sm"
          rows={6}
          placeholder="Call notes, follow-ups, context from the prospect…"
          value={noteDraft}
          onChange={(e) => setNoteDraft(e.target.value)}
        />
        <div className="mt-3 flex items-center gap-2">
          <Button
            size="sm"
            onClick={saveNote}
            disabled={saving || noteDraft === (lead.qualificationNote ?? '')}
          >
            <Save className="mr-1 h-4 w-4" /> {saving ? 'Saving…' : 'Save note'}
          </Button>
          {toast && <span className="text-xs text-muted-foreground">{toast}</span>}
        </div>
      </div>

      {/* Contacts (story 46.5 — add-contact button) */}
      {lead.accountId && (
        <div className="rounded-lg border bg-card p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Contacts at this account
            </h2>
            {!contactFormOpen && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setContactError(null);
                  setContactFormOpen(true);
                }}
              >
                <Plus className="mr-1 h-3 w-3" /> Add contact
              </Button>
            )}
          </div>
          {lead.contacts.length === 0 && !contactFormOpen && (
            <p className="mt-3 text-sm text-muted-foreground">
              No contacts at this account yet.
            </p>
          )}
          {lead.contacts.length > 0 && (
            <ul className="mt-3 divide-y">
              {lead.contacts.map((c) => (
                <li
                  key={c.id}
                  onClick={() =>
                    lead.accountId &&
                    navigate(`/accounts/${lead.accountId}?focusContact=${c.id}`)
                  }
                  onKeyDown={(e) => {
                    if ((e.key === 'Enter' || e.key === ' ') && lead.accountId) {
                      e.preventDefault();
                      navigate(`/accounts/${lead.accountId}?focusContact=${c.id}`);
                    }
                  }}
                  role={lead.accountId ? 'button' : undefined}
                  tabIndex={lead.accountId ? 0 : undefined}
                  className={`flex items-center justify-between py-2 text-sm transition-colors ${
                    lead.accountId
                      ? 'cursor-pointer rounded-md px-2 hover:bg-muted/40 focus:bg-muted/40 focus:outline-none'
                      : ''
                  }`}
                  title={lead.accountId ? t('contact.open_in_account') : undefined}
                >
                  <div>
                    <div className="font-medium">
                      {c.firstName} {c.lastName}
                    </div>
                    {c.title && (
                      <div className="text-xs text-muted-foreground">{c.title}</div>
                    )}
                  </div>
                  <div className="text-right text-xs text-muted-foreground">
                    {c.phone && <div>{c.phone}</div>}
                    {c.email && <div>{c.email}</div>}
                  </div>
                </li>
              ))}
            </ul>
          )}

          {contactFormOpen && (
            <div className="mt-4 space-y-3 rounded-md bg-muted/40 p-4">
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <Input
                  placeholder="First name"
                  value={contactFirstName}
                  onChange={(e) => setContactFirstName(e.target.value)}
                />
                <Input
                  placeholder="Last name"
                  value={contactLastName}
                  onChange={(e) => setContactLastName(e.target.value)}
                />
                <Input
                  placeholder="Title (optional)"
                  value={contactTitle}
                  onChange={(e) => setContactTitle(e.target.value)}
                />
                <Input
                  placeholder="Phone (optional)"
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                />
                <Input
                  className="sm:col-span-2"
                  placeholder="Email (optional)"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                />
              </div>
              {contactError && (
                <p className="text-xs text-destructive">{contactError}</p>
              )}
              <div className="flex justify-end gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setContactFormOpen(false)}
                  disabled={contactSaving}
                >
                  Cancel
                </Button>
                <Button size="sm" onClick={submitNewContact} disabled={contactSaving}>
                  {contactSaving ? 'Saving…' : 'Add contact'}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        {lead.status === 'NEW' && (
          <>
            <Button onClick={() => transition('qualify')} disabled={transitioning !== null}>
              Qualify
            </Button>
            <Button
              variant="outline"
              onClick={openDiscardDialog}
              disabled={transitioning !== null}
            >
              Discard
            </Button>
          </>
        )}
        {(lead.status === 'NEW' || lead.status === 'QUALIFIED') && !lead.convertedOpportunityId && (
          <Button onClick={openConvert} disabled={transitioning !== null || convertOpen}>
            Convert to Opportunity <ArrowRight className="ml-1 h-4 w-4" />
          </Button>
        )}
        {(lead.status === 'QUALIFIED' || lead.status === 'DISCARDED') && (
          <Button
            variant="outline"
            onClick={() => transition('revert')}
            disabled={transitioning !== null}
          >
            Revert to NEW
          </Button>
        )}
        {canHandoff && lead.status === 'QUALIFIED' && !lead.convertedOpportunityId && (
          handoffPending ? (
            <Button variant="outline" disabled>
              <Share2 className="mr-1 h-4 w-4" />
              {t('handoff.button_pending')}
            </Button>
          ) : (
            <Button
              variant="outline"
              onClick={() => { setHandoffNote(lead.qualificationNote ?? ''); setHandoffOpen(true); }}
              disabled={transitioning !== null}
            >
              <Share2 className="mr-1 h-4 w-4" />
              {t('handoff.button')}
            </Button>
          )
        )}
      </div>

      {/* Handoff dialog (story 59.2) */}
      {handoffOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-lg bg-card p-6 shadow-lg">
            <h2 className="text-lg font-semibold">{t('handoff.dialog_title')}</h2>
            <label className="mt-4 block text-sm font-medium">{t('handoff.note_label')}</label>
            <textarea
              rows={6}
              value={handoffNote}
              onChange={(e) => setHandoffNote(e.target.value)}
              className="mt-2 w-full rounded-md border bg-background p-3 text-sm"
              placeholder={t('handoff.note_placeholder')}
            />
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setHandoffOpen(false)} disabled={submittingHandoff}>
                {t('common.cancel')}
              </Button>
              <Button size="sm" onClick={submitHandoff} disabled={submittingHandoff}>
                {submittingHandoff ? t('handoff.submitting') : t('handoff.submit')}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="mt-4">
        {/* Enroll in sequence — story 61.4 */}
        <Button
          variant="outline"
          onClick={async () => {
            const res = await apiClient.get<{ id: string; name: string }[]>('/api/v1/sequences');
            setSequences(res.data);
            if (res.data.length > 0) setSelectedSequenceId(res.data[0].id);
            setEnrollOpen(true);
          }}
        >
          {t('sequences.enrollment.enroll_btn')}
        </Button>
      </div>

      {/* Sequence enrollment modal */}
      {enrollOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-sm rounded-lg bg-card p-6 shadow-lg space-y-4">
            <h2 className="text-lg font-semibold">{t('sequences.enrollment.title')}</h2>
            <div>
              <label className="block text-sm font-medium mb-1">{t('sequences.enrollment.select')}</label>
              <select
                className="w-full border rounded px-3 py-2 text-sm"
                value={selectedSequenceId}
                onChange={(e) => setSelectedSequenceId(e.target.value)}
              >
                {sequences.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setEnrollOpen(false)}>
                {t('sequences.enrollment.cancel')}
              </Button>
              <Button
                disabled={enrolling || !selectedSequenceId}
                onClick={async () => {
                  if (!id) return;
                  setEnrolling(true);
                  try {
                    await apiClient.post(`/api/v1/sequences/${selectedSequenceId}/enroll`, {
                      recipients: [{ recipientType: 'LEAD', recipientId: id }],
                    });
                    setEnrollOpen(false);
                    setToast(t('sequences.enrollment.success'));
                    setTimeout(() => setToast(null), 3000);
                  } finally {
                    setEnrolling(false);
                  }
                }}
              >
                {t('sequences.enrollment.enroll_btn')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Discard with farewell dialog */}
      {discardOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-lg bg-card p-6 shadow-lg">
            <h2 className="text-lg font-semibold">Discard lead</h2>
            <div className="mt-3 space-y-1">
              <label className="text-sm font-medium">
                {t('closeReason.label')} <span className="text-red-500">*</span>
              </label>
              <CloseReasonPicker
                value={discardReasonCode}
                onChange={(code, cat) => {
                  setDiscardReasonCode(code);
                  setDiscardReasonCategory(cat);
                }}
                allowDataQuality={allowDataQuality}
              />
              {discardReasonCategory === 'DATA_QUALITY' && (
                <p className="text-xs text-amber-600">
                  {t('closeReason.dq_warning')}
                </p>
              )}
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              {lead.campaignFarewellTemplate
                ? "Edit the campaign's default farewell before sending, or leave blank for a silent discard."
                : 'Write a closing note (optional). Leave blank to discard silently.'}
            </p>
            <textarea
              rows={5}
              value={farewellMessage}
              onChange={(e) => setFarewellMessage(e.target.value)}
              className="mt-2 w-full rounded-md border bg-background p-3 text-sm"
              placeholder="Thanks for your time. We're not the right fit for what you described, but please reach out if your needs change."
            />
            <div className="mt-3 flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDiscardOpen(false)}
                disabled={transitioning === 'discard'}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={submitDiscard}
                disabled={transitioning === 'discard' || !discardReasonCode}
              >
                {transitioning === 'discard' ? 'Discarding…' : 'Send & discard'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Convert-to-opportunity form */}
      {convertOpen && (
        <div className="rounded-lg border bg-card p-6">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Convert to Opportunity
          </h2>

          {templates === null ? (
            <p className="mt-3 text-sm text-muted-foreground">Loading templates…</p>
          ) : templates.length === 0 ? (
            <div className="mt-3 space-y-3 text-sm">
              <p>
                No process templates are available for this tenant. Add one before converting.
              </p>
              <Link to="/process-templates" className="text-primary underline">
                Open process templates →
              </Link>
              <div>
                <Button variant="outline" size="sm" onClick={() => setConvertOpen(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="mt-4 space-y-4">
              <div>
                <label htmlFor="opp-name" className="text-xs font-medium text-foreground">
                  Opportunity name
                </label>
                <Input
                  id="opp-name"
                  className="mt-1"
                  value={convertName}
                  onChange={(e) => setConvertName(e.target.value)}
                />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="opp-value" className="text-xs font-medium text-foreground">
                    Estimated value (USD)
                  </label>
                  <Input
                    id="opp-value"
                    className="mt-1"
                    type="number"
                    min="0"
                    step="100"
                    value={convertValue}
                    onChange={(e) => setConvertValue(e.target.value)}
                  />
                </div>
                <div>
                  <label htmlFor="opp-close" className="text-xs font-medium text-foreground">
                    Expected close date
                  </label>
                  <Input
                    id="opp-close"
                    className="mt-1"
                    type="date"
                    value={convertCloseDate}
                    onChange={(e) => setConvertCloseDate(e.target.value)}
                  />
                </div>
              </div>
              <div>
                <label htmlFor="opp-template" className="text-xs font-medium text-foreground">
                  Process template
                </label>
                <select
                  id="opp-template"
                  className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm"
                  value={convertTemplateId}
                  onChange={(e) => setConvertTemplateId(e.target.value)}
                >
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name} {t.scope === 'SYSTEM' ? '(system)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="opp-account" className="text-xs font-medium text-foreground">
                  Account {!lead.accountId && <span className="text-red-500">*</span>}
                </label>
                <AccountCombobox
                  className="mt-1"
                  value={convertAccountId || null}
                  onChange={(id) => setConvertAccountId(id ?? '')}
                  hint={lead.companyName}
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  {lead.accountId
                    ? 'Change here if the opportunity belongs elsewhere.'
                    : 'This lead has no account on file yet — pick or create one.'}
                </p>
              </div>

              {convertError && (
                <div
                  role="alert"
                  className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                >
                  {convertError}
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setConvertOpen(false)}
                  disabled={converting}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={submitConvert}
                  disabled={
                    converting ||
                    !convertName.trim() ||
                    !convertAccountId
                  }
                >
                  {converting ? 'Converting…' : 'Create opportunity'}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* AI SDR Draft card (Epic 66.1) */}
      <div className="rounded-lg border bg-card p-6">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {t('aiSdr.title')}
          </h2>
          <Button
            size="sm"
            variant="outline"
            onClick={generateAiSdrDraft}
            disabled={aiSdrLoading}
          >
            <Zap className="mr-1 h-3.5 w-3.5" />
            {aiSdrLoading ? t('aiSdr.generating') : t('aiSdr.generate_btn')}
          </Button>
        </div>
        {aiSdrError && (
          <p className="mt-2 text-xs text-destructive">{aiSdrError}</p>
        )}
        {aiSdrDraft && (
          <div className="mt-4 space-y-4">
            <div>
              <p className="text-xs font-semibold uppercase text-muted-foreground">
                {t('aiSdr.first_touch')}
              </p>
              <div className="mt-2 rounded bg-muted/40 p-3 text-sm">
                <p className="font-medium">{aiSdrDraft.firstTouchEmail.subject}</p>
                <p className="mt-1 whitespace-pre-wrap text-muted-foreground">
                  {aiSdrDraft.firstTouchEmail.body}
                </p>
              </div>
            </div>
            {aiSdrDraft.followUpCadence.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase text-muted-foreground">
                  {t('aiSdr.follow_up_cadence')}
                </p>
                <div className="mt-2 space-y-2">
                  {aiSdrDraft.followUpCadence.map((draft, i) => (
                    <div key={i} className="rounded bg-muted/40 p-3 text-sm">
                      <p className="text-xs text-muted-foreground">
                        {t('aiSdr.day_label', { n: (i + 1) * 3 })}
                      </p>
                      <p className="font-medium">{draft.subject}</p>
                      <p className="mt-1 whitespace-pre-wrap text-muted-foreground">{draft.body}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {aiSdrDraft.nextBestAction && (
              <div>
                <p className="text-xs font-semibold uppercase text-muted-foreground">
                  {t('aiSdr.next_best_action')}
                </p>
                <p className="mt-1 text-sm">{aiSdrDraft.nextBestAction}</p>
              </div>
            )}
          </div>
        )}
      </div>

      <AttachmentPanel entityType="LEAD" entityId={lead.id} />
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-0.5 font-medium">{value}</div>
    </div>
  );
}

/**
 * Read-only display row used inside the "All fields" section.
 *
 * Renders an em-dash for null/empty values so the field still shows a
 * box (better than collapsing the layout when nothing's there).
 */
function ReadField({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}) {
  const display = value && value.length > 0 ? value : '—';
  return (
    <div className="space-y-1">
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm">{display}</div>
    </div>
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}
